import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Megaphone,
  Plus,
  Edit,
  Eye,
  Calendar,
  FileText,
  Send,
  Save,
  Trash2,
  CheckCircle,
  Clock,
  AlertTriangle,
  XCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { announcementsAPI, filesAPI } from '../../services/api';
import { bondTrusteesAPI } from '../../services/bondTrusteesAPI';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const Announcements = () => {
  const { user } = useAuth() || {};
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedBond, setSelectedBond] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [existingAttachmentFileIds, setExistingAttachmentFileIds] = useState([]);
  const [removedFileIds, setRemovedFileIds] = useState([]);
  const [removedPbByFileId, setRemovedPbByFileId] = useState({});

  const [bonds, setBonds] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [annLoading, setAnnLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [status, setStatus] = useState('draft');
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [infoModal, setInfoModal] = useState({ title: '', message: '', type: 'info' });
  const [processingId, setProcessingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [announcementToDelete, setAnnouncementToDelete] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  const openModal = (title, message, type = 'info') => {
    setInfoModal({ title, message, type });
    setShowInfoModal(true);
  };

  const updateAnnouncementInList = (updated) => {
    if (!updated) return;
    setAnnouncements(prev => {
      const next = prev.map(a => {
        const matches = ((a._id && (a._id === (updated._id || updated.id))) || (a.id && (a.id === (updated._id || updated.id))));
        if (!matches) return a;
        const merged = { ...a, ...updated };
        if (typeof updated?.bondId === 'string' && typeof a?.bondId === 'object') {
          merged.bondId = a.bondId;
        }
        if (!merged.bondName && a.bondName) merged.bondName = a.bondName;
        if (typeof updated?.l1ApprovedBy === 'string' && typeof a?.l1ApprovedBy === 'object') {
          merged.l1ApprovedBy = a.l1ApprovedBy;
        }
        if (typeof updated?.l2ApprovedBy === 'string' && typeof a?.l2ApprovedBy === 'object') {
          merged.l2ApprovedBy = a.l2ApprovedBy;
        }
        return merged;
      });
      return next.sort((a, b) => new Date(b.createdAt || b.publishedAt || 0) - new Date(a.createdAt || a.publishedAt || 0));
    });
  };

  const isCurrentUserL1Approver = (announcement) => {
    if (!announcement || !user) return false;
    const currentIds = [user?._id, user?.id, user?.userId].filter(Boolean).map(String);
    let l1CandidateIds = [];
    const l1 = announcement.l1ApprovedBy;
    if (!l1) return false;
    if (typeof l1 === 'string') {
      l1CandidateIds.push(l1);
    } else if (typeof l1 === 'object') {
      l1CandidateIds.push(l1?._id, l1?.id, l1?.userId);
    }
    l1CandidateIds = l1CandidateIds.filter(Boolean).map(String);
    return l1CandidateIds.some(id => currentIds.includes(id));
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setAnnLoading(true);
      
      const trusteeId = user?._id || user?.id;
      if (!trusteeId) {
        setAnnLoading(false);
        setLoading(false);
        return;
      }

      const bondsPromise = bondTrusteesAPI.getTrusteeBonds(trusteeId);
      const annPromise = announcementsAPI.getAnnouncements({ limit: 20 });

      try {
        const annResp = await annPromise;
        const annList = annResp?.data?.data?.announcements || annResp?.data?.announcements || [];
        const sorted = [...annList].sort((a, b) => {
          const aTime = new Date(a.createdAt || a.publishedAt || 0).getTime();
          const bTime = new Date(b.createdAt || b.publishedAt || 0).getTime();
          return bTime - aTime;
        });
        setAnnouncements(sorted);
      } catch (e) {
        console.error('Failed to load announcements', e);
        setAnnouncements([]);
      } finally {
        setAnnLoading(false);
      }

      try {
        const bondsResp = await bondsPromise;
        const assignments = bondsResp?.data?.data || bondsResp?.data || [];
        const normalizedBonds = Array.isArray(assignments)
          ? assignments
              .filter(assignment => assignment.status === 'active' && assignment.bondId)
              .map(assignment => ({
                id: assignment.bondId?._id || assignment.bondId?.id || assignment.bondId,
                name: assignment.bondId?.bondName || assignment.bondId?.name,
                isin: assignment.bondId?.isin
              }))
          : [];
        setBonds(normalizedBonds);
      } catch (e) {
        console.error('Failed to load bonds', e);
        setBonds([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  if (annLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <LoadingSpinner />
      </div>
    );
  }

  const performSave = async (desiredStatus) => {
    if (!selectedBond) return;
    try {
      setSubmitting(true);
      const form = new FormData();
      form.append('title', title);
      form.append('content', content);
      form.append('bondId', selectedBond);
      attachments.forEach((file) => { if (file && file.name) form.append('attachments', file, file.name); });
      form.append('status', desiredStatus);
      if (editingId) {
        const resp = await announcementsAPI.updateAnnouncement(editingId, form);
        const updated = resp?.data?.data?.announcement || resp?.data?.announcement;
        if (updated) setAnnouncements(prev => {
          const replaced = prev.map(a => (a._id === updated._id || a.id === updated.id) ? updated : a);
          return replaced.sort((a, b) => new Date(b.createdAt || b.publishedAt || 0) - new Date(a.createdAt || a.publishedAt || 0));
        });
        if (Array.isArray(removedFileIds) && removedFileIds.length > 0) {
          for (const fid of removedFileIds) {
            try {
              await announcementsAPI.removeAttachment(editingId, fid);
              let pbId = removedPbByFileId[fid];
              if (!pbId) {
                try {
                  const metaResp = await filesAPI.getFile(fid);
                  const meta = metaResp?.data?.data?.file || metaResp?.data?.file || metaResp?.data;
                  pbId = meta?.pbRecordId || meta?.pbrecordId || meta?.pb_id || meta?.pbRecordID;
                } catch { }
              }
              if (pbId) { try { await filesAPI.deletePocketBaseRecord(pbId); } catch { } }
            } catch (delErr) {
              console.warn('[Announcements] Failed to delete attachment on submit', fid, delErr?.message || delErr);
            }
          }
        }
      } else {
        const resp = await announcementsAPI.createAnnouncement(form);
        const newAnn = resp?.data?.data?.announcement || resp?.data?.announcement;
        if (newAnn) setAnnouncements(prev => {
          const next = [newAnn, ...prev];
          return next.sort((a, b) => new Date(b.createdAt || b.publishedAt || 0) - new Date(a.createdAt || a.publishedAt || 0));
        });
      }
      setTitle('');
      setContent('');
      setSelectedBond('');
      setAttachments([]);
      setExistingAttachments([]);
      setExistingAttachmentFileIds([]);
      setRemovedFileIds([]);
      setRemovedPbByFileId({});
      setEditingId(null);
      setStatus('draft');
      setShowCreateForm(false);
      setValidationErrors({});
    } catch (e) {
      console.error('Save announcement failed', e);
      openModal('Save Failed', e?.response?.data?.message || 'Failed to save announcement', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};
    if (title.trim().length < 5) {
      errors.title = 'Title must be at least 5 characters long';
    }
    if (content.trim().length < 10) {
      errors.content = 'Content must be at least 10 characters long';
    }
    if (!selectedBond) {
      errors.bond = 'Please select a bond';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    setValidationErrors({});
    await performSave('pending_l1');
  };

  const handleSaveDraft = async () => {
    await performSave('draft');
  };

  const beginEdit = (announcement) => {
    const id = announcement._id || announcement.id;
    setEditingId(id);
    setShowCreateForm(true);
    const bondId = announcement.bondId?._id || announcement.bondId?.id || announcement.bondId || '';
    setSelectedBond(bondId);
    setTitle(announcement.title || '');
    setContent(announcement.content || '');
    setStatus(announcement.status || 'draft');
    const atts = Array.isArray(announcement.attachments) ? announcement.attachments : [];
    setExistingAttachments(atts);
    const fileIds = announcement.attachmentsFileIds || announcement.attachmentFileIds || [];
    setExistingAttachmentFileIds(Array.isArray(fileIds) ? fileIds : []);
    setAttachments([]);
    setValidationErrors({});
  };

  const handleDelete = (announcement) => {
    if (!announcement) return;
    setAnnouncementToDelete(announcement);
    setShowDeleteModal(true);
  };

  const confirmDeleteAnnouncement = async () => {
    if (!announcementToDelete) return;
    const id = announcementToDelete._id || announcementToDelete.id;
    if (!id) return;
    try {
      await announcementsAPI.deleteAnnouncement(id);
      setAnnouncements(prev => prev.filter(a => (a._id || a.id) !== id));
      setShowDeleteModal(false);
      setAnnouncementToDelete(null);
    } catch (e) {
      console.error('Delete announcement failed', e);
      openModal('Delete Failed', e?.response?.data?.message || 'Failed to delete announcement', 'error');
    }
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setAttachments([...attachments, ...files]);
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const getExistingFileIdByIndex = (att, idx) => {
    return att?.fileId || att?.id || existingAttachmentFileIds?.[idx];
  };

  const removeExistingAttachment = async (att, idx) => {
    const identifier = getExistingFileIdByIndex(att, idx);
    if (!identifier) {
      console.warn('[Announcements] Missing file identifier for attachment removal.');
      return;
    }
    setExistingAttachments(prev => prev.filter((_, i) => i !== idx));
    setExistingAttachmentFileIds(prev => prev.filter((_, i) => i !== idx));
    setRemovedFileIds(prev => Array.from(new Set([...(prev || []), identifier])));
    if (att?.pbRecordId) {
      setRemovedPbByFileId(prev => ({ ...prev, [identifier]: att.pbRecordId }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Announcements</h1>
              <p className="text-sm text-secondary-600 mt-1">Create and manage special announcements for bond investors</p>
            </div>
            <button
              onClick={() => {
                setEditingId(null);
                setShowCreateForm(!showCreateForm);
                setValidationErrors({});
              }}
              
              className="btn-primary flex items-center space-x-2 w-full sm:w-auto justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" />
              <span>Create Announcement</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

      {/* Announcement History */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <div className="px-6 py-4 flex items-center justify-between border-b border-gray-200 bg-white">
          <h2 className="text-lg font-medium text-gray-900">Announcements ({announcements.length})</h2>
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <Calendar className="w-4 h-4" /> <span>All time</span>
          </div>
        </div>
        {announcements.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">Title</th>
                  <th className="table-header-cell">Bond</th>
                  <th className="table-header-cell">Status</th>
                  <th className="table-header-cell">Approval L1</th>
                  <th className="table-header-cell">Approval L2</th>
                  <th className="table-header-cell">Date</th>
                  <th className="table-header-cell">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {announcements.map(announcement => {
                  const id = announcement._id || announcement.id;
                  const dateStr = new Date(announcement.publishedAt || announcement.createdAt).toLocaleDateString();
                  const statusConfig = {
                    draft: { color: 'gray', label: 'Draft', icon: FileText },
                    pending_l1: { color: 'yellow', label: 'Pending L1', icon: Clock },
                    pending_l2: { color: 'amber', label: 'Pending L2', icon: Clock },
                    published: { color: 'green', label: 'Published', icon: CheckCircle }
                  };
                  const getStatusBadge = (status) => {
                    const cfg = statusConfig[status] || { color: 'gray', label: status, icon: FileText };
                    const Icon = cfg.icon;
                    return (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${cfg.color}-100 text-${cfg.color}-800`}>
                        <Icon className="w-3 h-3 mr-1" />
                        {cfg.label}
                      </span>
                    );
                  };
                  const l1 = announcement.l1ApprovedBy;
                  const l2 = announcement.l2ApprovedBy;
                  const l1At = announcement.l1ApprovedAt;
                  const l2At = announcement.l2ApprovedAt;
                  const nameFromUser = (u) => {
                    if (!u) return '';
                    const first = u?.personalInfo?.firstName; const last = u?.personalInfo?.lastName;
                    if (first || last) return [first, last].filter(Boolean).join(' ');
                    return u?.corporateInfo?.companyName || u?.email || '';
                  };
                  const l1Name = nameFromUser(l1);
                  const l2Name = nameFromUser(l2);
                  return (
                    <tr key={id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap max-w-[260px]">
                        <div className="text-sm font-medium text-gray-900 truncate" title={announcement.title}>{announcement.title}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{announcement.bondId?.bondName || announcement.bondName || '—'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(announcement.status)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {l1Name ? (
                          <div className="text-xs text-gray-700">
                            <div className="font-medium">{l1Name}</div>
                            {l1At && <div className="text-gray-500 text-[10px]">{new Date(l1At).toLocaleDateString()}</div>}
                          </div>
                        ) : <span className="text-gray-400 text-xs">-</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {l2Name ? (
                          <div className="text-xs text-gray-700">
                            <div className="font-medium">{l2Name}</div>
                            {l2At && <div className="text-gray-500 text-[10px]">{new Date(l2At).toLocaleDateString()}</div>}
                          </div>
                        ) : <span className="text-gray-400 text-xs">-</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{dateStr}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => { }}
                            className="text-primary-600 hover:text-primary-700 p-1"
                            onMouseDown={(e) => e.preventDefault()}
                            title="View Details"
                          >
                            <Link to={`/trustee/announcements/${id}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </button>
                          {true && (
                            <>
                              {announcement.status !== 'published' && (
                              <button
                                onClick={() => beginEdit(announcement)}
                                className="text-secondary-600 hover:text-secondary-700 p-1"
                                title="Edit Announcement"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                              )}
                              {announcement.status === 'draft' && (
                                <button
                                  onClick={async () => {
                                    try {
                                      setProcessingId(id);
                                      const resp = await announcementsAPI.submitForL1(id);
                                      const updated = resp?.data?.data?.announcement || resp?.data?.announcement;
                                      updateAnnouncementInList(updated || { ...announcement, status: 'pending_l1' });
                                      openModal('Submitted', 'Announcement moved to L1 approval queue.', 'success');
                                    } catch (err) {
                                      openModal('Submit Failed', err?.response?.data?.message || 'Submit failed', 'error');
                                    } finally {
                                      setProcessingId(null);
                                    }
                                  }}
                                  disabled={processingId === id}
                                  className={`p-1 ${processingId === id ? 'text-gray-400 cursor-not-allowed' : 'text-primary-600 hover:text-primary-700'}`}
                                  title="Submit for L1 Approval"
                                >
                                  <Send className="h-4 w-4" />
                                </button>
                              )}
                              <button
                                onClick={() => handleDelete(announcement)}
                                className="text-error-600 hover:text-error-700 p-1"
                                title="Delete Announcement"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                              {announcement.status === 'pending_l1' && (
                                <button
                                  onClick={async () => {
                                    try {
                                      setProcessingId(id);
                                      const resp = await announcementsAPI.approveL1(id);
                                      const updated = resp?.data?.data?.announcement || resp?.data?.announcement;
                                      const fallback = { ...announcement, status: 'pending_l2', l1ApprovedBy: announcement.l1ApprovedBy || { _id: user?._id || user?.id } };
                                      updateAnnouncementInList(updated || fallback);
                                      openModal('L1 Approved', 'Announcement advanced to L2 approval stage.', 'success');
                                    } catch (err) {
                                      openModal('L1 Approval Failed', err?.response?.data?.message || 'L1 approve failed', 'error');
                                    } finally {
                                      setProcessingId(null);
                                    }
                                  }}
                                  disabled={processingId === id}
                                  className="btn-primary btn-sm"
                                  title="Approve L1"
                                >
                                  Approve L1
                                </button>
                              )}
                              {announcement.status === 'pending_l2' && !isCurrentUserL1Approver(announcement) && (
                                  <button
                                    onClick={async () => {
                                      try {
                                        setProcessingId(id);
                                        const resp = await announcementsAPI.approveL2(id);
                                        const updated = resp?.data?.data?.announcement || resp?.data?.announcement;
                                        updateAnnouncementInList(updated || { ...announcement, status: 'published', publishedAt: new Date().toISOString() });
                                        openModal('Published', 'Announcement has been published.', 'success');
                                      } catch (err) {
                                        const serverMsg = err?.response?.data?.message || 'L2 approve failed';
                                        const debug = [];
                                        try { debug.push(`CurrentUser: ${user?._id || user?.id}`); } catch { }
                                        try { debug.push(`Announcement.l1ApprovedBy: ${typeof announcement.l1ApprovedBy === 'object' ? (announcement.l1ApprovedBy?._id || announcement.l1ApprovedBy?.id) : announcement.l1ApprovedBy}`); } catch { }
                                        openModal('L2 Approval Failed', `${serverMsg}\n${debug.join('\n')}`, 'error');
                                      } finally {
                                        setProcessingId(null);
                                      }
                                    }}
                                  disabled={processingId === id}
                                  className="btn-primary btn-sm"
                                  title="Approve L2 & Publish"
                                >
                                  Approve L2
                                  </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No announcements yet</h3>
            <p className="mt-1 text-sm text-gray-500">Create your first announcement to communicate with investors.</p>
            <button
              onClick={() => {
                setEditingId(null);
                setShowCreateForm(true);
                setValidationErrors({});
              }}
              
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Create First Announcement
            </button>
          </div>
        )}
      </div>

      {showInfoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4 p-5">
            <div className="flex items-start mb-3">
              {infoModal.type === 'success' && <CheckCircle className="h-5 w-5 text-green-600 mr-2" />}
              {infoModal.type === 'warning' && <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />}
              {infoModal.type === 'error' && <XCircle className="h-5 w-5 text-red-600 mr-2" />}
              {infoModal.type === 'info' && <FileText className="h-5 w-5 text-blue-600 mr-2" />}
              <h3 className="text-sm font-medium text-gray-900 leading-5">{infoModal.title || 'Notice'}</h3>
            </div>
            <div className="text-xs text-gray-700 whitespace-pre-line mb-4">{infoModal.message}</div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowInfoModal(false)}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && announcementToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">Delete Announcement</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete this announcement?
                This action cannot be undone.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <div className="flex">
                  <AlertTriangle className="h-5 w-5 text-yellow-400" />
                  <div className="ml-3">
                    <p className="text-sm text-yellow-800">
                      Title: {announcementToDelete.title || 'Untitled'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setAnnouncementToDelete(null);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteAnnouncement}
                className="btn-primary"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Announcement Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">{editingId ? 'Edit Announcement' : 'Create New Announcement'}</h3>
            </div>
            <div className="px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Associated Bond */}
                  <div>
                    <label className="form-label">Associated Bond *</label>
                    <select
                      value={selectedBond}
                      onChange={(e) => {
                        setSelectedBond(e.target.value);
                        setValidationErrors(prev => ({ ...prev, bond: undefined }));
                      }}
                      className={`form-input ${validationErrors.bond ? 'border-red-500' : ''}`}
                      required
                    >
                      <option value="">Select a bond</option>
                      {bonds.map((bond) => (
                        <option key={bond.id} value={bond.id}>
                          {bond.name}
                        </option>
                      ))}
                    </select>
                    {validationErrors.bond && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.bond}</p>
                    )}
                  </div>
                </div>

                {/* Title */}
                <div>
                  <label className="form-label">Announcement Title *</label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      setValidationErrors(prev => ({ ...prev, title: undefined }));
                    }}
                    className={`form-input ${validationErrors.title ? 'border-red-500' : ''}`}
                    placeholder="Enter announcement title"
                    required
                  />
                  <div className="mt-1 flex justify-between items-center">
                    <span className="text-xs text-secondary-500">Minimum 5 characters</span>
                    <span className="text-xs text-secondary-400">{title.length} characters</span>
                  </div>
                  {validationErrors.title && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.title}</p>
                  )}
                </div>

                {/* Content */}
                <div>
                  <label className="form-label">Announcement Content *</label>
                  <textarea
                    value={content}
                    onChange={(e) => {
                      setContent(e.target.value);
                      setValidationErrors(prev => ({ ...prev, content: undefined }));
                    }}
                    className={`form-input ${validationErrors.content ? 'border-red-500' : ''}`}
                    rows="8"
                    placeholder="Write your announcement content here..."
                    required
                  />
                  <div className="mt-1 flex justify-between items-center">
                    <span className="text-xs text-secondary-500">Minimum 10 characters. You can use simple formatting.</span>
                    <span className="text-xs text-secondary-400">{content.length} characters</span>
                  </div>
                  {validationErrors.content && (
                    <p className="mt-1 text-sm text-red-600">{validationErrors.content}</p>
                  )}
                </div>

                {/* Attachments */}
                <div>
                  <label className="form-label">Attachments (Optional)</label>
                  {editingId && existingAttachments.length > 0 && (
                    <div className="mb-3">
                      <div className="text-sm text-secondary-600 mb-1">Existing Attachments</div>
                      <div className="space-y-2">
                        {existingAttachments.map((att, idx) => (
                          <div key={idx} className="flex items-center justify-between bg-secondary-50 p-2 rounded">
                            <div className="flex items-center">
                              <FileText className="w-4 h-4 text-secondary-400 mr-2" />
                              {att?.url || att?.fileUrl || att?.pbRecordId ? (
                                <a href={att.url || att.fileUrl || (att.pbRecordId ? `/api/files/pbrecord/${att.pbRecordId}/content` : '#')}
                                  target="_blank" rel="noreferrer" className="text-sm text-primary-600 hover:underline">
                                  {att.fileName || att.filename || att.originalName || att.name}
                                </a>
                              ) : (
                                <span className="text-sm text-secondary-700">{att.fileName || att.filename || att.originalName || att.name}</span>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={() => removeExistingAttachment(att, idx)}
                              className="text-error-500 hover:text-error-700 text-sm"
                              disabled={!getExistingFileIdByIndex(att, idx)}
                              title={!getExistingFileIdByIndex(att, idx) ? 'Cannot remove this attachment (no fileId)' : 'Remove'}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="border-2 border-dashed rounded-lg p-4" style={{ borderColor: '#d1d5db' }}>
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp,.bmp,.tiff,.svg,.csv"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="attachment-upload"
                    />
                    <label htmlFor="attachment-upload" className="cursor-pointer flex flex-col items-center">
                      <FileText className="w-6 h-6 text-primary mb-2" />
                      <span className="text-sm text-secondary-600">
                        Click to upload files
                      </span>
                      <span className="text-xs text-secondary-500 mt-1">
                        PDF, DOC, DOCX, JPG, PNG, CSV (max 10MB each)
                      </span>
                    </label>
                  </div>

                  {/* Attachment List */}
                  {attachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {attachments.map((file, index) => (
                        <div key={index} className="flex items-center justify-between bg-secondary-50 p-2 rounded">
                          <div className="flex items-center">
                            <FileText className="w-4 h-4 text-secondary-400 mr-2" />
                            <span className="text-sm text-secondary-700">{file.name}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className="text-error-500 hover:text-error-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 sm:justify-end pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setEditingId(null);
                      setExistingAttachments([]);
                      setAttachments([]);
                      setTitle('');
                      setContent('');
                      setSelectedBond('');
                      setRemovedFileIds([]);
                      setRemovedPbByFileId({});
                      setValidationErrors({});
                    }}
                    className="btn-secondary order-2 sm:order-1"
                  >
                    Cancel
                  </button>
                  {!editingId && (
                    <button
                      type="button"
                      onClick={handleSaveDraft}
                      disabled={submitting}
                      className="btn-secondary order-3 sm:order-2 flex items-center justify-center disabled:opacity-50"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save as Draft
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={submitting || !selectedBond || !title.trim() || !content.trim()}
                    className="btn-primary order-1 sm:order-3 flex items-center justify-center disabled:opacity-50"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {submitting ? (editingId ? 'Updating...' : 'Submitting...') : (editingId ? 'Update' : 'Submit')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Announcements;
